import { Check, ArrowUpRight, Gauge } from "lucide-react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";

/* The Portfolio Standard, scored: five frames, measurements, reel, credits, description.
   Everything reads from the live user object, so the meter moves as they work. */
export default function BookStrength() {
  const { user } = useAuth();
  if (!user) return null;
  const p = user.profile || {};

  const frames = (p.photos || []).length;
  const meas = ["height_cm", "bust", "waist", "hips"].filter((k) => p[k]).length;
  const hasReel = (p.reels || []).length > 0;
  const credits = (p.work_history || []).length;
  const bioLen = (p.bio || "").trim().length;

  const checks = [
    { key: "frames", label: "Five frames", ok: frames >= 5,
      hint: frames >= 5 ? "All five frames are in — order them deliberately, they become your comp card." : `${frames} of 5 — headshot, profile, full length, three-quarter, editorial.` },
    { key: "measurements", label: "Measurements", ok: meas === 4,
      hint: meas === 4 ? "Height, bust, waist and hips — all present." : `${meas} of 4 — height, bust, waist, hips. Houses cast by numbers first.` },
    { key: "reel", label: "Video reel", ok: hasReel,
      hint: hasReel ? "Your walking video is up." : "30–60 seconds: daylight, plain backdrop, name and height to camera." },
    { key: "credits", label: "Credits", ok: credits > 0,
      hint: credits > 0 ? `${credits} credit${credits === 1 ? "" : "s"} on record.` : "One real credit — a test shoot counts." },
    { key: "description", label: "Description", ok: bioLen >= 40,
      hint: bioLen >= 40 ? "Your description is written." : "Two to four sentences: lane, stats, proof, availability." },
  ];
  const done = checks.filter((c) => c.ok).length;
  const pct = Math.round((done / checks.length) * 100);

  return (
    <section className="panel p-8 mb-16" data-testid="book-strength">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <p className="overline-label mb-2 flex items-center gap-2"><Gauge size={14} className="text-accent" /> Book strength</p>
          <p className="font-display text-3xl" data-testid="book-strength-score">
            {done === checks.length ? (
              <>Full <span className="italic text-accent">standard</span></>
            ) : (
              <>{done} of {checks.length} <span className="italic text-accent">to standard</span></>
            )}
          </p>
        </div>
        <Link
          to="/portfolio-guide"
          data-testid="book-strength-guide-link"
          className="font-grotesk text-[10px] uppercase tracking-[0.18em] text-accent hover:text-white transition-colors inline-flex items-center gap-2"
        >
          The Portfolio Standard <ArrowUpRight size={13} />
        </Link>
      </div>

      <div className="h-px bg-white/10 relative mb-8 overflow-visible">
        <motion.div
          className="absolute left-0 top-0 h-px bg-accent"
          initial={false}
          animate={{ width: `${pct}%` }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          data-testid="book-strength-meter"
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-x-8 gap-y-5">
        {checks.map((c) => (
          <div key={c.key} className="flex items-start gap-3" data-testid={`book-strength-check-${c.key}`}>
            {c.ok ? (
              <Check size={15} className="text-emerald-400 shrink-0 mt-0.5" />
            ) : (
              <ArrowUpRight size={15} className="text-accent shrink-0 mt-0.5" />
            )}
            <div>
              <p className={`font-grotesk text-[10px] uppercase tracking-[0.15em] mb-1 ${c.ok ? "text-white/85" : "text-accent"}`}>
                {c.label}
              </p>
              <p className="text-xs text-white/45 leading-relaxed">{c.hint}</p>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
